import { ref, onMounted, getCurrentInstance } from 'vue';
import { getListFieldApprovalKTT, getListApprovalKTT, approveTNAByKTT, rejectTNAByKTT, getUniqueCompetencies, getAvailableCompanies } from './api';

export function useApprovalKTT() {
  const instance = getCurrentInstance();
  const swal = instance?.proxy?.$swal;
  const trainings = ref([]);
  const uniqueCompetencies = ref([]);
  const employees = ref([]);
  const availableCompanies = ref([]);
  const selectedCompany = ref(null);
  const pagination = ref({
    totalItems: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const loading = ref(false);
  const actionLoading = ref(false);
  const loadingCompanies = ref(false);
  const error = ref(null);

  // Get token from URL query parameter (Vue 2 compatible)
  const token = instance?.proxy.$route?.query?.token;

  // // Fetch training fields (columns)
  // async function fetchTrainingFields() {
  //   if (!token) {
  //     error.value = 'Token tidak ditemukan di URL';
  //     return;
  //   }

  //   try {
  //     loading.value = true;
  //     const response = await getListApprovalKTT({ token });

  //     if (response.httpCode === 200 && response.data) {
  //       trainings.value = response.data.map(item => ({
  //         id: item.id,
  //         title: item.title
  //       }));
  //     }
  //   } catch (err) {
  //     error.value = 'Gagal memuat data training';
  //     console.error(err);
  //   } finally {
  //     loading.value = false;
  //   }
  // }

  // Fetch employee approval data
  async function fetchEmployeeData(params = {}) {
    if (!token) {
      error.value = 'Token tidak ditemukan di URL';
      return;
    }

    try {
      loading.value = true;
      const page = params.page || pagination.value.page || 1;
      const limit = params.limit || pagination.value.limit || 10;
      const company = params.company !== undefined ? params.company : selectedCompany.value;
      
      const response = await getListApprovalKTT({ 
        token, 
        page, 
        limit,
        company 
      });

      if (response.httpCode === 200 && response.data) {
        employees.value = response.data.data || [];
        pagination.value = {
          totalItems: response.data.totalItems || 0,
          page: response.data.page || page,
          limit: response.data.limit || limit,
          totalPages: response.data.totalPages || 1,
        };
      }
    } catch (err) {
      error.value = 'Gagal memuat data karyawan';
      console.error(err);
    } finally {
      loading.value = false;
    }
  }

  async function fetchAllEmployeeData(params = {}) {
    if (!token) {
      error.value = 'Token tidak ditemukan di URL';
      return [];
    }

    const company = params.company !== undefined ? params.company : selectedCompany.value;
    const response = await getListApprovalKTT({
      token,
      page: 1,
      limit: pagination.value.limit || 10,
      all: true,
      company,
    });

    if (response?.httpCode === 200 && response.data) {
      return response.data.data || [];
    }

    return [];
  }

  async function fetchUniqueCompetencyData(params = {}) {
    if (!token) {
      return;
    }

    try {
      const company = params.company !== undefined ? params.company : selectedCompany.value;
      const response = await getUniqueCompetencies({ token, company });

      if (response.httpCode === 200 && Array.isArray(response.data)) {
        uniqueCompetencies.value = response.data;
      }
    } catch (err) {
      console.error('Gagal memuat unique competencies:', err);
      uniqueCompetencies.value = [];
    }
  }

  // Fetch available companies
  async function fetchAvailableCompanies() {
    if (!token) {
      return;
    }

    try {
      loadingCompanies.value = true;
      const response = await getAvailableCompanies({ token });

      if (Array.isArray(response.data)) {
        availableCompanies.value = response.data;
        console.log('Available companies:', availableCompanies.value);
      }
    } catch (err) {
      console.error('Error fetching available companies:', err);
      availableCompanies.value = [];
    } finally {
      loadingCompanies.value = false;
    }
  }

  // Handle company filter change
  async function onCompanyFilterChange(company) {
    selectedCompany.value = company;
    await Promise.all([
      fetchUniqueCompetencyData({ company }),
      fetchEmployeeData({
        page: 1,
        limit: pagination.value.limit,
        company
      })
    ]);
  }

  // Update status per employee per training
  function updateStatus(empId, trainingId, newStatus) {
    const emp = employees.value.find(e => e.id === empId);
    if (emp) {
      if (!emp.status) emp.status = {};
      emp.status[trainingId] = newStatus;
    }
  }

  // Handle approve selected TNA submissions
  async function handleApproveAPI(ids = []) {
    try {
      actionLoading.value = true;
      const response = await approveTNAByKTT({ token, ids });

      if (response && response.httpCode === 200) {
        // Refresh data with current filters
        await fetchEmployeeData({
          page: pagination.value.page,
          limit: pagination.value.limit,
          company: selectedCompany.value
        });
        swal?.('Success', 'Pengajuan TNA berhasil disetujui.', 'success');
        return true;
      } else {
        swal?.('Error', 'Pengajuan TNA belum berhasil disetujui', 'error');
        return false;
      }
    } catch (err) {
      console.error('Error approving TNA:', err);
      swal?.('Error', 'Terjadi kesalahan saat menyetujui pengajuan TNA', 'error');
      return false;
    } finally {
      actionLoading.value = false;
    }
  }

  // Handle reject selected TNA submissions with reason
  async function handleRejectAPI(reason, ids = []) {
    try {
      actionLoading.value = true;
      const response = await rejectTNAByKTT({
        token,
        reason,
        ids,
      });

      if (response && response.httpCode === 200) {
        // Refresh data with current filters
        await fetchEmployeeData({
          page: pagination.value.page,
          limit: pagination.value.limit,
          company: selectedCompany.value
        });
        swal?.('Success', 'Pengajuan TNA berhasil ditolak.', 'success');
        return true;
      } else {
        swal?.('Error', 'Pengajuan TNA belum berhasil ditolak', 'error');
        return false;
      }
    } catch (err) {
      console.error('Error rejecting TNA:', err);
      swal?.('Error', 'Terjadi kesalahan saat menolak pengajuan TNA', 'error');
      return false;
    } finally {
      actionLoading.value = false;
    }
  }

  // Initialize data on mount
  onMounted(async () => {
    // await fetchTrainingFields();
    await fetchUniqueCompetencyData();
    await fetchAvailableCompanies();
    await fetchEmployeeData();
  });

  return {
    employees,
    trainings,
    uniqueCompetencies,
    availableCompanies,
    selectedCompany,
    pagination,
    loading,
    actionLoading,
    loadingCompanies,
    error,
    updateStatus,
    token,
    fetchEmployeeData,
    fetchAllEmployeeData,
    handleApproveAPI,
    handleRejectAPI,
    onCompanyFilterChange,
  };
}
